import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { SettingsLayout } from "@/components/dashboard/SettingsLayout";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { NotificationSettings, PeriodicSyncSettings } from "@/components/pwa";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/hooks/useProfile";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { 
  ArrowLeft, 
  Camera, 
  CheckCircle, 
  Loader2, 
  User,
  Shield,
  CreditCard,
  Download,
  Smartphone,
  Link2,
  Key,
  Eye,
  EyeOff
} from "lucide-react";



const ProfileSettings = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, loading: profileLoading, refetch } = useProfile();
  const { isInstallable, isInstalled, promptInstall } = usePWAInstall();
  
  const [name, setName] = useState(profile?.name || "");

  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Password States
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSettingPassword, setIsSettingPassword] = useState(false);

  // Update name when profile loads
  useState(() => {
    if (profile?.name) {
      setName(profile.name);
    }
  });

  const handleUpdateProfile = async () => {
    try {
      setIsSaving(true);
      const { error } = await supabase
        .from("profiles")
        .update({ name: name })
        .eq("id", user?.id);

      if (error) throw error;

      toast({
        title: t("settings.profile.updated"),
        description: t("settings.profile.updatedDesc"),
      });
    } catch (error) {
      console.error("Profile update error:", error);
      toast({
        title: t("settings.profile.error"),
        description: t("settings.profile.updateError"),
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: t("settings.profile.error"),
        description: t("settings.profile.imageSizeError"),
        variant: "destructive",
      });
      return;
    }

    try {
      setIsUploading(true);
      
      const fileExt = file.name.split(".").pop();
      const filePath = `${user.id}/avatar.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from("avatars")
        .getPublicUrl(filePath);

      // Update profile
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: publicUrl })
        .eq("id", user.id);

      if (updateError) throw updateError;

      await refetch();
      toast({
        title: t("settings.profile.avatarUpdated"),
        description: t("settings.profile.avatarUpdatedDesc"),
      });
    } catch (error) {
      console.error("Avatar upload error:", error);
      toast({
        title: t("settings.error"),
        description: t("settings.profile.avatarError"),
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Handle Set Password for OAuth users
  const handleSetPassword = async () => {
    // Validation
    if (newPassword.length < 8) {
      toast({
        title: t("settings.password.error"),
        description: t("settings.password.minLength"),
        variant: "destructive",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: t("settings.password.error"),
        description: t("settings.password.mismatch"),
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSettingPassword(true);
      
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      toast({
        title: t("settings.password.success"),
        description: t("settings.password.successDesc"),
      });

      // Clear form
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : undefined;
      console.error("Set password error:", error);
      toast({
        title: t("settings.password.error"),
        description: message || t("settings.password.errorDesc"),
        variant: "destructive",
      });
    } finally {
      setIsSettingPassword(false);
    }
  };

  const getInitials = () => {
    if (profile?.name) {
      return profile.name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
    }
    return user?.email?.[0].toUpperCase() || "U";
  };

  // Check if user logged in via OAuth (Google, etc.)
  const isOAuthUser = user?.app_metadata?.provider !== 'email';

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold">{t("settings.title")}</h1>
        <p className="text-muted-foreground">{t("settings.subtitle")}</p>
      </div>
      
      <SettingsLayout>
        {profileLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Avatar Section */}
            <Card>
              <CardHeader>
                <CardTitle>{t("settings.profile.avatar")}</CardTitle>
                <CardDescription>{t("settings.profile.avatarDesc")}</CardDescription>
              </CardHeader>
              <CardContent className="flex items-center gap-6">
                <div className="relative">
                  <Avatar className="w-24 h-24">
                    <AvatarImage src={profile?.avatar_url || undefined} />
                    <AvatarFallback className="text-2xl">{getInitials()}</AvatarFallback>
                  </Avatar>
                  <Button
                    size="icon"
                    variant="secondary"
                    className="absolute -bottom-2 -right-2 rounded-full w-8 h-8"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                  >
                    {isUploading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Camera className="w-4 h-4" />
                    )}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarUpload}
                  />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t("settings.profile.avatarHint")}
                  </p>
                </div>
              </CardContent>
            </Card>
            
            {/* Profile Info */}
            <Card>
              <CardHeader>
                <CardTitle>{t("settings.profile.info")}</CardTitle>
                <CardDescription>{t("settings.profile.infoDesc")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t("settings.profile.name")}</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("settings.profile.namePlaceholder")}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t("settings.profile.email")}</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="email"
                      value={user?.email || ""}
                      disabled
                      className="bg-muted"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t("settings.profile.emailHint")}
                  </p>
                </div>

                <div className="flex justify-end pt-4">
                  <Button onClick={handleUpdateProfile} disabled={isSaving}>
                    {isSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t("settings.saving")}
                      </>
                    ) : (
                      t("settings.save")
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Set Password Section - Only show for OAuth users */}
            {isOAuthUser && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Key className="w-5 h-5" />
                    {t("settings.password.title")}
                  </CardTitle>
                  <CardDescription>
                    {t("settings.password.desc")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">
                      {t("settings.password.new")}
                    </Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        type={showPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder={t("settings.password.newPlaceholder")}
                        className="pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">
                      {t("settings.password.confirm")}
                    </Label>
                    <Input
                      id="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder={t("settings.password.confirmPlaceholder")}
                    />
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button 
                      onClick={handleSetPassword} 
                      disabled={isSettingPassword || !newPassword || !confirmPassword}
                    >
                      {isSettingPassword ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t("settings.password.setting")}
                        </>
                      ) : (
                        <>
                          <Key className="w-4 h-4 mr-2" />
                          {t("settings.password.setButton")}
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </SettingsLayout>
    </div>
  );
};

export default ProfileSettings;
